import React from 'react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { teamMembers } from '../data/mock';
import { Mail } from 'lucide-react';

function TeamCard({ member, index }) {
  const [ref, isVisible] = useScrollReveal({ threshold: 0.1 });

  return (
    <div
      ref={ref}
      className={`bg-[#0d0d0d] border border-white/5 rounded-xl p-6 group hover:border-emerald-500/20 transition-all duration-500 ${
      isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`
      }
      style={{ transitionDelay: `${index * 60}ms` }}>

      {/* Card Header */}
      <div className="flex items-center justify-between mb-4">
        <span className="font-mono text-[9px] tracking-[0.2em] text-white/20 uppercase">
          Go2 Media
        </span>
        <div className="grid grid-cols-2 gap-0.5 opacity-20">
          <div className="w-2 h-2 bg-emerald-500/50 rounded-sm" />
          <div className="w-2 h-2 bg-emerald-500/50 rounded-sm" />
          <div className="w-2 h-2 bg-emerald-500/50 rounded-sm" />
          <div className="w-2 h-2 bg-emerald-500/20 rounded-sm" />
        </div>
      </div>

      <div className="w-full h-[1px] bg-white/5 mb-8" />

      {/* Photo */}
      <div className="flex justify-center mb-6">
        <div className="relative w-28 h-28 md:w-32 md:h-32">
          <div className="absolute inset-0 rounded-full border border-emerald-500/15 group-hover:border-emerald-500/30 transition-colors" />
          <img
            src={member.image}
            alt={member.name}
            className="w-full h-full rounded-full object-cover opacity-80 group-hover:opacity-100 transition-all duration-700"
            onError={(e) => {
              e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(member.name)}&size=256&background=111&color=10b981`;
            }} />

        </div>
      </div>

      {/* Name & Role */}
      <div className="text-center">
        <h3 className="text-lg md:text-xl font-bold uppercase tracking-[0.05em] text-white group-hover:text-emerald-400 transition-colors">
          {member.name}
        </h3>
        <p className="font-mono text-[10px] tracking-[0.2em] text-white/30 uppercase mt-1">
          {member.role}
        </p>
      </div>

      {/* Hover Details */}
      <div className="mt-4 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
        <div className="text-center">
          <span className="font-mono text-[9px] tracking-[0.15em] text-emerald-500/40">
            Focus: {member.focus}
          </span>
        </div>
        <a
          href={`mailto:${member.email}`}
          className="flex items-center justify-center gap-2 mt-3 font-mono text-[10px] tracking-[0.1em] text-white/30 hover:text-emerald-400 transition-colors">

          <Mail size={12} />
          EMAIL ::{member.email.split('@')[0]}
        </a>
      </div>
    </div>);

}

function CareersBlock() {
  const [ref, isVisible] = useScrollReveal({ threshold: 0.2 });

  return (
    <div
      ref={ref}
      className={`mt-16 md:mt-24 bg-[#0d0d0d] border border-white/5 rounded-2xl p-8 md:p-12 lg:p-16 transition-all duration-1000 ${
      isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`
      }>

      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
        <div className="flex-1">
          <span className="font-mono text-[10px] tracking-[0.2em] text-emerald-500/40">
            Status: Always Scouting
          </span>
          <h3 className="text-3xl md:text-4xl lg:text-5xl font-black text-white mt-4 tracking-[-0.02em]">
            Join The <br />
            <span className="text-emerald-400/40">Winning Team.</span>
          </h3>
          <p className="font-mono text-xs leading-relaxed text-white/35 mt-6 max-w-lg">
            We're building the future of iGaming affiliate marketing. We need sharp minds across SEO, data, content, and engineering. If you live and breathe performance marketing and want to work with the best operators in the world — your seat is ready.
          </p>
        </div>

        <div className="flex items-end gap-6">
          <div className="flex items-center gap-4">
            <img
              src="https://ui-avatars.com/api/?name=MR&size=96&background=111&color=10b981&bold=true"
              alt="Matevz R."
              className="w-12 h-12 rounded-full object-cover" />

            <div>
              <div className="font-mono text-xs text-white/60">Matevz R.</div>
              <div className="font-mono text-[10px] text-white/25 tracking-[0.15em]">FOUNDER & CEO</div>
            </div>
          </div>
          <a
            href="mailto:careers@go2media.io?subject=Joining%20the%20Team"
            className="font-mono text-xs tracking-[0.15em] px-6 py-3 border border-emerald-500/30 rounded-full text-emerald-400 hover:bg-emerald-500 hover:text-black transition-all duration-300 whitespace-nowrap">

            Initiate Sequence
          </a>
        </div>
      </div>
    </div>);

}

export default function TeamSection() {
  const [headerRef, headerVisible] = useScrollReveal({ threshold: 0.15 });

  return (
    <section id="team" className="bg-[#050505] py-20 md:py-32">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10">
        {/* Section Header */}
        <div
          ref={headerRef}
          className={`mb-16 transition-all duration-1000 ${
          headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`
          }>

          <span className="font-mono text-[10px] tracking-[0.3em] text-emerald-500/50 uppercase">
            The Team
          </span>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mt-3">
            <h2 className="text-3xl md:text-5xl lg:text-6xl font-black tracking-[-0.02em] text-white">
              Who <span className="text-white/20">We Are</span>
            </h2>
            <div className="flex gap-8">
              <div className="text-right">
                <div className="font-mono text-[10px] tracking-[0.15em] text-white/30">
                  Status:<span className="text-emerald-400 ml-1">Leading Innovation</span>
                </div>
              </div>
              <div className="text-right">
                <div className="font-mono text-[10px] tracking-[0.15em] text-white/30">
                  Core Team:<span className="text-white/70 ml-1">8</span>
                </div>
              </div>
              <div className="text-right">
                <div className="font-mono text-[10px] tracking-[0.15em] text-white/30">
                  Expert Network:<span className="text-white/70 ml-1">80+</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Team Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {teamMembers.map((member, i) =>
          <TeamCard key={member.id} member={member} index={i} />
          )}
        </div>

        {/* Careers Block */}
        <CareersBlock />
      </div>
    </section>);

}